import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";

interface Address {
  country: string;
  city: string;
  firstLine: string;
  postCode: string;
}

interface BasePayoutMethod {
  fullName: string;
  legalType: string;
  currency: string;
  createdAt?: string;
  updatedAt?: string;
}

interface SkrillPayout extends BasePayoutMethod {
  payoutMethod: "skrill";
  skrillEmail: string;
}

interface WiseEurIbanPayout extends BasePayoutMethod {
  payoutMethod: "wise_eur_iban";
  iban: string;
  bic?: string;
  address: Address;
}

interface PaypalPayout extends BasePayoutMethod {
  payoutMethod: "paypal";
  paypalEmail: string;
}

interface WiseUsdSwiftPayout extends BasePayoutMethod {
  payoutMethod: "wise_usd_swift";
  swiftCode: string;
  accountNumber: string;
  address: Address;
}

type PayoutMethod =
  | SkrillPayout
  | WiseEurIbanPayout
  | PaypalPayout
  | WiseUsdSwiftPayout;

interface PayoutMethodsMap {
  [key: string]: PayoutMethod;
}

interface PayoutMethodState {
  payoutMethods: PayoutMethodsMap;
  loading: boolean;
  error: string | null;
}

const initialState: PayoutMethodState = {
  payoutMethods: {},
  loading: false,
  error: null,
};

// Thunk to fetch payoutMethods for a user
export const fetchPayoutMethods = createAsyncThunk<
  PayoutMethodsMap,
  { userId: string },
  { rejectValue: string }
>("payoutMethods/fetch", async ({ userId }, { rejectWithValue }) => {
  try {
    const docRef = doc(db, "payoutMethods", userId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return {};
    const data = docSnap.data();
    return data.payoutMethods || {};
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

// Thunk to upsert/add a payout method under the user's document
export const upsertPayoutMethod = createAsyncThunk<
  { payoutMethodKey: string; payoutMethod: PayoutMethod },
  { userId: string; payoutMethodKey: string; payoutMethod: PayoutMethod },
  { rejectValue: string }
>(
  "payoutMethods/upsert",
  async ({ userId, payoutMethodKey, payoutMethod }, { rejectWithValue }) => {
    try {
      const now = new Date().toISOString();

      const withTimestamps = {
        ...payoutMethod,
        createdAt: payoutMethod.createdAt || now,
         payoutMethod: payoutMethodKey as any, 
        updatedAt: now,
      };

      const docRef = doc(db, "payoutMethods", userId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        await setDoc(docRef, {
          payoutMethods: {
            [payoutMethodKey]: withTimestamps,
          },
        });
      } else {
        await setDoc(
          docRef,
          {
            payoutMethods: {
              [payoutMethodKey]: withTimestamps,
            },
          },
          { merge: true }
        );
      }

      return { payoutMethodKey, payoutMethod: withTimestamps };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const payoutMethodSlice = createSlice({
  name: "payoutMethods",
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearPayoutMethods(state) {
      state.payoutMethods = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayoutMethods.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayoutMethods.fulfilled, (state, action) => {
        state.loading = false;
        state.payoutMethods = action.payload;
      })
      .addCase(fetchPayoutMethods.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to fetch payout methods";
      })

      .addCase(upsertPayoutMethod.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(upsertPayoutMethod.fulfilled, (state, action) => {
        state.loading = false;
        const { payoutMethodKey, payoutMethod } = action.payload;
        state.payoutMethods[payoutMethodKey] = payoutMethod;
      })
      .addCase(upsertPayoutMethod.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to upsert payout method";
      });
  },
});

export const { clearError, clearPayoutMethods } = payoutMethodSlice.actions;
export default payoutMethodSlice.reducer;
